// 問診票の現在状態 (定義・ユーザー情報・送信ID 等) を集約管理する reactive store
;(function () {
  const state = Vue.reactive({
    hash: null,           // URL クエリの ?hash=
    md5tableEntry: null,  // { uid, title, publickey }
    userId: null,
    qName: null,
    qstDef: null,         // 問診票定義 (definitions/{uid}/{qName})
    userProperty: null,   // 医療機関プロフィール (users/{uid})
    answerId: null,
    alreadySent: false,
    mailNotification: false,
    publicKey: null
  });

  function reset() {
    state.hash = null;
    state.md5tableEntry = null;
    state.userId = null;
    state.qName = null;
    state.qstDef = null;
    state.userProperty = null;
    state.answerId = null;
    state.alreadySent = false;
    state.mailNotification = false;
    state.publicKey = null;
  }

  /**
   * Firebase RTDB から md5table → users → definitions の順に読み込み、state を更新
   */
  async function loadByHash(hash) {
    const db = firebase.database();
    const md5snap = await db.ref('questionnaire/md5table/' + hash).once('value');
    const entry = md5snap.val();
    if (!entry) throw new Error('指定された問診票が見つかりませんでした。QR コード / URL が正しいか、有効期限が切れていないかご確認ください。');

    state.hash = hash;
    state.md5tableEntry = entry;
    state.userId = entry.uid;
    state.qName = entry.title;
    // 公開鍵は施設本人しか書けない users/{uid}/publickey から取る (md5table の publickey は使わない)。
    // 無い = 管理者が鍵をリセットし施設が未再ログイン。 紛失した古い鍵で暗号化しないよう受付を止める
    state.publicKey = (await db.ref('users/' + entry.uid + '/publickey').once('value')).val();
    if (!state.publicKey) throw new Error('現在この問診票は受け付けできません。医療機関にお問い合わせください。');

    // users/{uid} は非公開 (loginid・支払情報を含む)。 患者画面で使う項目だけを個別に読む
    // (database.rules.json で項目ごとに公開している。 追加するときは両方を揃えること)
    const PUBLIC_USER_FIELDS = [
      'name', 'logo', 'privacyPolicy', 'welcomeMessage', 'endingMessage', 'privacyItems',
      'privacyItemsRequired', 'mailNotification', 'representative', 'zipcode', 'address',
      'mail', 'tel1', 'tel2', 'fax', 'url', 'subscriptionStatus', 'subscription/feewaiver'
    ];
    const userVals = await Promise.all(
      PUBLIC_USER_FIELDS.map((k) => db.ref('users/' + entry.uid + '/' + k).once('value').then((s) => s.val()))
    );
    const userProperty = {};
    PUBLIC_USER_FIELDS.forEach((k, i) => {
      if (userVals[i] === null) return;
      if (k === 'subscription/feewaiver') userProperty.subscription = { feewaiver: userVals[i] };
      else userProperty[k] = userVals[i];
    });
    state.userProperty = userProperty;

    // 旧旧版踏襲 (RenderQuestionnaire.vue:1052-1059, 1126-1137 getImg):
    // userProperty.logo が画像名 (data:image で始まらない) の場合、
    // questionnaire/images/{name} から base64 データを取得して差し替える。
    // これにより <img :src="userProperty.logo"> がそのまま描画できる。
    if (state.userProperty && typeof state.userProperty.logo === 'string' && state.userProperty.logo) {
      const logo = state.userProperty.logo;
      if (!logo.startsWith('data:image')) {
        try {
          const imgSnap = await db.ref('questionnaire/images/' + logo).once('value');
          const resolved = imgSnap.val();
          if (resolved) state.userProperty.logo = resolved;
        } catch (e) {
          console.warn('[questionnaireStore] logo resolve failed for "' + logo + '":', e);
        }
      }
    }

    const escQName = window.mixinUtils.escapeFbRtdbKey(entry.title);
    const defSnap = await db.ref(`questionnaire/definitions/${entry.uid}/${escQName}`).once('value');
    state.qstDef = defSnap.val();
    if (!state.qstDef) throw new Error('問診票のデータを取得できませんでした。医療機関にお問い合わせください。');

    // mail_notification: 定義側 > ユーザー設定 の順で優先
    if (typeof state.qstDef.mail_notification === 'boolean') {
      state.mailNotification = state.qstDef.mail_notification;
    } else {
      state.mailNotification = !!(state.userProperty && state.userProperty.mailNotification);
    }

    state.answerId = generateAnswerId();
    return state;
  }

  function generateAnswerId() {
    // 旧旧版踏襲 (Q4CL-client_old_version.xml:3195):
    //   String(Math.floor(Number(now) / 100)).slice(-6)
    // = 現在時刻 (ms) / 100 (0.1秒単位) を文字列化して末尾 6 桁。 約 27 時間周期。
    // 例: 1750403468123 → 17504034681 → 末尾 6 桁 = "034681"
    const now = Date.now();
    return String(Math.floor(now / 100)).slice(-6);
  }

  window.questionnaireStore = {
    state,
    reset,
    loadByHash,
    generateAnswerId
  };
})();
